import { formatDate } from '@/lib/formatDate';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, Search, Plus, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { StatusChip, type StatusTone } from '@/components/ui/status-chip';
import { searchPatients, getPatientDeposits, getPatientInvoices } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { displayAge } from '@/lib/patientAge';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BillingService {
  id: string;
  name: string;
  price: number;
  tax_rate?: number;
  category: string;
  is_frequent?: boolean;
  is_active?: boolean;
}

interface Patient {
  id: string;
  name: string;
  phone?: string;
  address?: string;
  age?: number | string;
  date_of_birth?: string;
  gender?: string;
  mrn?: string;
  doctor_id?: string | null;
}

interface Doctor {
  id: string;
  name: string;
}

interface BillItem {
  id: string;
  service_id?: string;
  service_name: string;
  category?: string;
  item_type?: 'consultation' | 'procedure' | 'investigation' | 'other';
  quantity: number;
  unit_price: number;
  discount: number;
  tax_rate: number;
  tax_amount: number;
  total_price: number;
}

interface MedicalBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient?: Patient | null;
  services: BillingService[];
  onCreateInvoice: (invoiceData: {
    items: Array<Omit<BillItem, 'id' | 'discount'> & { discount_amount: number }>;
    // Always the computed rupee amount (never the raw percent value) — the
    // backend subtracts discount_amount as-is; discount_type is display metadata.
    discount: number;
    discountType: 'flat' | 'percent';
    /** Why the bill or any line was discounted; required with a discount */
    discountReason?: string;
    taxAmount: number;
    paidAmount: number;
    payments: Array<{
      amount: number;
      payment_mode: 'cash' | 'upi' | 'card' | 'cheque' | 'advance';
      reference_number?: string;
      upi_app?: string;
    }>;
    finalTotal: number;
    doctorId?: string;
    patient?: Patient;
    invoiceDate?: string;
    notes?: string;
    balance_forward?: number;
    balance_forward_settle?: number;
    balance_forward_payment_mode?: string;
    depositAmount?: number;
    depositPaymentMode?: string;
  }) => void;
  loading?: boolean;
  clinicId: string;
  doctorId?: string;
  doctors?: Doctor[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

const PAYMENT_METHODS = ['Cash', 'UPI', 'Card', 'Cheque'];

const ITEM_TYPE_MAP: Record<string, 'consultation' | 'procedure' | 'investigation' | 'other'> = {
  consultation: 'consultation',
  procedure: 'procedure',
  investigation: 'investigation',
  diagnostic: 'investigation',
};

function lineTax(quantity: number, unitPrice: number, taxRate: number) {
  return Math.round(quantity * unitPrice * (taxRate / 100) * 100) / 100;
}

interface PaymentRowState {
  id: string;
  mode: string;
  amount: number;
  referenceNumber: string;
  upiApp: string;
}

function makePaymentRow(mode: string = 'Cash', amount = 0): PaymentRowState {
  return {
    id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
    mode, amount, referenceNumber: '', upiApp: '',
  };
}

function localToday(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function deriveStatus(paid: number, total: number): 'Paid' | 'Partial' | 'Pending' {
  if (total <= 0) return 'Pending';
  if (paid >= total) return 'Paid';
  if (paid > 0) return 'Partial';
  return 'Pending';
}

const inr = (n: number) =>
  `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Shorter form for the footer button: no paise on whole amounts
const inrShort = (n: number) =>
  Number.isInteger(n) ? `₹${n.toLocaleString('en-IN')}` : inr(n);

// Items grid — as the prescription pad's medicine table: # · Service · Qty · Price · (GST %) · Discount · Amount · ✕
const ITEM_GRID     = 'grid grid-cols-[24px_minmax(0,1fr)_56px_96px_80px_96px_28px] gap-x-2 px-3';
const ITEM_GRID_GST = 'grid grid-cols-[24px_minmax(0,1fr)_56px_96px_56px_80px_96px_28px] gap-x-2 px-3';
// Below 640px each item takes two lines (name, then labelled Qty / Price / Discount)
const STACKED_QUERY = '(max-width: 639px)';

// Numbers in the items table read as text until hovered or focused (pad style); always boxed when stacked
const cellInput = (stacked: boolean) => cn(
  'h-8 touch:h-10 w-full min-w-0 rounded-md border bg-transparent px-2 text-sm font-mono tabular-nums text-right',
  'focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/20',
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
  stacked ? 'border-field bg-card' : 'border-transparent hover:border-field touch:border-field',
);
const selectClass = 'h-9 touch:h-10 rounded-md border border-field bg-card px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50';
const noSignKeys = (e: React.KeyboardEvent<HTMLInputElement>) => ['e', 'E', '-', '+'].includes(e.key) && e.preventDefault();

function SummaryLine({ label, value, strong = false, className }: {
  label: React.ReactNode; value: React.ReactNode; strong?: boolean; className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-3 text-sm', className)}>
      <span className={strong ? 'font-semibold text-foreground' : 'text-label'}>{label}</span>
      <span className={cn('font-mono tabular-nums', strong ? 'font-semibold text-foreground' : 'text-foreground')}>{value}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MedicalBillingModal({
  isOpen, onClose,
  patient = null, services = [],
  onCreateInvoice, loading = false,
  clinicId, doctorId, doctors = [],
}: MedicalBillingModalProps) {
  const { currentUser } = useAuth();
  const isFrontDesk = !currentUser?.doctor_id;
  const { can } = usePermission();
  const canDiscount = can('billing:discount');

  // ── Bill state ───────────────────────────────────────────────────────────
  const [billItems, setBillItems]       = useState<BillItem[]>([]);
  const [discount, setDiscount]         = useState(0);
  const [discountType, setDiscountType] = useState<'flat' | 'percent'>('flat');
  const [discountReason, setDiscountReason] = useState('');
  const [payments, setPayments]         = useState<PaymentRowState[]>(() => [makePaymentRow()]);
  const [error, setError]               = useState<string | null>(null);
  const [billDate, setBillDate]         = useState(() => localToday());
  const [notes, setNotes]               = useState('');

  // ── Patient search ───────────────────────────────────────────────────────
  const [selectedPatient, setSelectedPatient]   = useState<Patient | null>(patient ?? null);
  const [patientQuery, setPatientQuery]         = useState('');
  const [patientResults, setPatientResults]     = useState<Patient[]>([]);
  const [patientSearching, setPatientSearching] = useState(false);
  const [availableAdvance, setAvailableAdvance] = useState(0);
  const [outstanding, setOutstanding]           = useState(0);
  const [pendingAmount, setPendingAmount]       = useState(0);
  const patientInputRef = useRef<HTMLInputElement>(null);
  const prevFinalTotalRef = useRef(0);

  const todayStr = useMemo(() => localToday(), []);
  const isBackdated = useMemo(() => {
    if (!billDate) return false;
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    return new Date(billDate) < cutoff;
  }, [billDate]);

  // ── Service search (inline at bottom of table) ───────────────────────────
  const [serviceSearch, setServiceSearch]         = useState('');
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const serviceSearchRef  = useRef<HTMLInputElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);
  const [serviceActive, setServiceActive] = useState(0);

  // On tablets the bill shrinks to the area above the on-screen keyboard; cap the list so its
  // last rows ("Add as custom item") aren't clipped by the bill or hidden behind the keyboard
  useEffect(() => {
    const list = serviceDropdownRef.current;
    const vv = window.visualViewport;
    if (!serviceDropdownOpen || !list) return;
    const fit = () => {
      const dialogBottom = list.closest('[role="dialog"]')?.getBoundingClientRect().bottom ?? window.innerHeight;
      const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const room = Math.min(dialogBottom, visibleBottom) - list.getBoundingClientRect().top - 8;
      list.style.maxHeight = `${Math.max(120, Math.min(256, room))}px`;
    };
    fit();
    vv?.addEventListener('resize', fit);
    return () => vv?.removeEventListener('resize', fit);
  }, [serviceDropdownOpen, serviceSearch]);

  const [stacked, setStacked] = useState(() => window.matchMedia(STACKED_QUERY).matches);
  useEffect(() => {
    const mql = window.matchMedia(STACKED_QUERY);
    const onChange = () => setStacked(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // ── Balance forward (Add to Bill) ───────────────────────────────────────
  const [balanceForward, setBalanceForward] = useState(0);

  // ── Deposit collection ───────────────────────────────────────────────────
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositOpen, setDepositOpen]     = useState(false);
  const [discountOpen, setDiscountOpen]   = useState(false);
  // Payment rows whose optional reference box was opened
  const [refOpen, setRefOpen]             = useState<Record<string, boolean>>({});

  // ── Doctor / misc ────────────────────────────────────────────────────────
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId ?? '');
  const [unitPriceInputs, setUnitPriceInputs]   = useState<Record<string, string>>({});

  // ── Sync props ───────────────────────────────────────────────────────────
  useEffect(() => { setSelectedPatient(patient ?? null); }, [patient]);
  useEffect(() => { setSelectedDoctorId(doctorId ?? ''); }, [doctorId]);

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setBillItems([]); setDiscount(0); setDiscountType('flat'); setDiscountReason('');
      setPayments([makePaymentRow()]); setError(null);
      setPatientQuery(''); setPatientResults([]);
      setBillDate(localToday());
      setServiceSearch(''); setServiceDropdownOpen(false);
      setNotes(''); setUnitPriceInputs({});
      setAvailableAdvance(0); setOutstanding(0); setPendingAmount(0); setBalanceForward(0);
      setDepositAmount(0); setDepositOpen(false); setDiscountOpen(false); setRefOpen({});
      prevFinalTotalRef.current = 0; prevBillTotalRef.current = 0;
      setSelectedPatient(patient ?? null);
      setSelectedDoctorId(doctorId ?? '');
    }
  }, [isOpen]);

  // ── Close service dropdown on outside click ──────────────────────────────
  useEffect(() => {
    if (!serviceDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target as Node) &&
        serviceSearchRef.current && !serviceSearchRef.current.contains(e.target as Node)
      ) {
        setServiceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [serviceDropdownOpen]);

  // ── Calculations ─────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => billItems.reduce((s, i) => s + (parseFloat(String(i.total_price)) || 0), 0),
    [billItems]
  );

  const discountAmount = useMemo(() => {
    if (discountType === 'percent') return Math.min(subtotal, (subtotal * discount) / 100);
    return Math.min(subtotal, Math.max(0, discount));
  }, [discount, discountType, subtotal]);
  // A line discount counts too: either needs a reason
  const anyDiscount = discountAmount > 0 || billItems.some(i => i.discount > 0);

  const taxTotal = useMemo(
    () => billItems.reduce((s, i) => s + (i.tax_amount || 0), 0),
    [billItems]
  );

  const finalTotal = useMemo(
    () => Math.max(0, subtotal - discountAmount + taxTotal),
    [subtotal, discountAmount, taxTotal]
  );

  const paidAmount = useMemo(
    () => payments.reduce((s, p) => s + (p.amount || 0), 0),
    [payments]
  );

  // totalToCollect = invoice total + any explicitly added balance forward + deposit to collect
  const totalToCollect = finalTotal + balanceForward + depositAmount;
  // balanceAmount = total still to collect (bill + settlement + deposit combined)
  const balanceAmount  = Math.max(0, totalToCollect - paidAmount);
  // Excess collected toward the balance forward settlement
  const settlementAmount = useMemo(
    () => balanceForward > 0 ? Math.min(Math.max(0, paidAmount - finalTotal), balanceForward) : 0,
    [paidAmount, finalTotal, balanceForward]
  );
  // True overpayment = collected more than the total to collect (error)
  const trueOverpaid = useMemo(
    () => Math.max(0, paidAmount - totalToCollect),
    [paidAmount, totalToCollect]
  );
  const hasGST = useMemo(() => billItems.some(i => i.tax_rate > 0), [billItems]);
  // This bill's own part of the payment (cap at finalTotal)
  const billPaidAmount = Math.min(paidAmount, finalTotal);
  // Next to "To collect": paid against everything collected now — earlier dues and a deposit too,
  // so ₹100 of a ₹9,147 collection reads "Part paid", not "Paid"
  const paymentStatus  = deriveStatus(Math.min(paidAmount, totalToCollect), totalToCollect);

  // Auto-fill payments when the bill changes, as long as the user hasn't typed over them:
  // an advance row follows the bill up to the advance available, the one other row takes
  // what's left (added as Cash when the advance no longer covers everything)
  const prevBillTotalRef = useRef(0);
  useEffect(() => {
    const prevTotal = prevFinalTotalRef.current;
    const prevBill  = prevBillTotalRef.current;
    setPayments(rows => {
      const sumAdv = (rs: PaymentRowState[]) => rs.filter(r => r.mode === 'Advance').reduce((s, r) => s + (r.amount || 0), 0);
      let next = rows;
      const adv = rows.find(r => r.mode === 'Advance');
      if (adv && adv.amount === Math.min(availableAdvance, prevBill)) {
        next = next.map(r => r.id === adv.id ? { ...r, amount: Math.min(availableAdvance, finalTotal) } : r);
      }
      const rest = Math.max(0, totalToCollect - sumAdv(next));
      const open = next.filter(r => r.mode !== 'Advance');
      if (open.length === 1) {
        const prevRest = Math.max(0, prevTotal - sumAdv(rows));
        if (open[0].amount === prevRest && prevRest !== rest) {
          next = next.map(r => r.id === open[0].id ? { ...r, amount: rest } : r);
        }
      } else if (open.length === 0 && adv && rest > 0) {
        next = [...next, makePaymentRow('Cash', rest)];
      }
      return next;
    });
    prevFinalTotalRef.current = totalToCollect;
    prevBillTotalRef.current  = finalTotal;
  }, [totalToCollect, finalTotal]);


  // ── Last consultation fee (last 15 days) ─────────────────────────────────
  // Many clinics see a review free or cheaper within some days of a visit; the desk decides, with the facts
  const [lastConsult, setLastConsult] = useState<{ date: string; amount: number; doctor: string | null } | null>(null);
  useEffect(() => {
    setLastConsult(null);
    if (!selectedPatient?.id || !can('billing:read')) return;
    let cancelled = false;
    const from = new Date(Date.now() - 15 * 86400000).toLocaleDateString('en-CA');
    getPatientInvoices(selectedPatient.id, { from })
      .then(list => {
        if (cancelled || !Array.isArray(list)) return;
        for (const inv of list) {   // newest first
          if (inv.status === 'cancelled') continue;
          const fee = (inv.items ?? []).filter((it: any) => /consult/i.test(it.category ?? ''))
            .reduce((sum: number, it: any) => sum + Number(it.total_price || 0), 0);
          if (fee > 0) { setLastConsult({ date: String(inv.invoice_date).slice(0, 10), amount: fee, doctor: inv.doctor_name ?? null }); return; }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedPatient?.id, can]);
  const lastConsultDays = lastConsult
    ? Math.round((new Date(`${localToday()}T00:00:00`).getTime() - new Date(`${lastConsult.date}T00:00:00`).getTime()) / 86400000)
    : null;

  // ── Patient account snapshot ──────────────────────────────────────────────
  useEffect(() => {
    if (!selectedPatient?.id || !clinicId) { setAvailableAdvance(0); setOutstanding(0); return; }
    let cancelled = false;
    getPatientDeposits(selectedPatient.id, clinicId)
      .then(res => {
        if (cancelled) return;
        setAvailableAdvance(Number(res?.available_advance || 0));
        setOutstanding(Number(res?.outstanding || 0));
        setPendingAmount(Number(res?.pending_amount || 0));
      })
      .catch(() => { if (!cancelled) { setAvailableAdvance(0); setOutstanding(0); } });
    return () => { cancelled = true; };
  }, [selectedPatient?.id, clinicId]);

  // Picking the patient here also picks their doctor when none is chosen yet
  const pickPatient = (p: Patient) => {
    setSelectedPatient(p); setPatientQuery(''); setPatientResults([]);
    if (!selectedDoctorId && p.doctor_id && doctors.some(d => d.id === p.doctor_id)) setSelectedDoctorId(p.doctor_id);
  };

  // ── Patient search ────────────────────────────────────────────────────────
  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      if (q.trim().length < 2) { setPatientResults([]); setPatientSearching(false); return; }
      setPatientSearching(true);
      try {
        const res = await searchPatients(q, clinicId);
        setPatientResults(Array.isArray(res) ? res.slice(0, 8) : []);
      } catch { setPatientResults([]); }
      finally { setPatientSearching(false); }
    }, 300),
    [clinicId]
  );

  useEffect(() => {
    if (patientQuery.trim().length >= 2) { setPatientSearching(true); debouncedSearch(patientQuery); }
    else setPatientResults([]);
  }, [patientQuery, debouncedSearch]);

  // ── Bill item operations ──────────────────────────────────────────────────

  // base = qty * price - line discount (pre-tax subtotal)
  function lineBase(qty: number, unitPrice: number, discount: number) {
    return Math.max(0, qty * unitPrice - discount);
  }

  const addService = (svc: BillingService) => {
    if (!svc.is_active) { setError('This service is inactive'); return; }
    setError(null);
    const price   = parseFloat(String(svc.price))    || 0;
    const taxRate = parseFloat(String(svc.tax_rate)) || 0;
    const existing = billItems.find(i => i.service_id === svc.id);
    if (existing) {
      updateQty(existing.id, existing.quantity + 1);
    } else {
      const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
      setBillItems(prev => [...prev, {
        id, service_id: svc.id, service_name: svc.name,
        category: svc.category, item_type: ITEM_TYPE_MAP[svc.category] ?? 'other',
        quantity: 1, unit_price: price, discount: 0,
        tax_rate: taxRate, tax_amount: lineTax(price, 1, taxRate), total_price: price,
      }]);
    }
  };

  const updateQty = (id: string, qty: number) => {
    if (qty <= 0) { removeItem(id); return; }
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(qty, i.unit_price, i.discount);
      return { ...i, quantity: qty, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updatePrice = (id: string, price: number) => {
    const p = Math.max(0, parseFloat(String(price)) || 0);
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(i.quantity, p, i.discount);
      return { ...i, unit_price: p, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updateDiscount = (id: string, discount: number) => {
    const d = Math.max(0, discount);
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      const base = lineBase(i.quantity, i.unit_price, d);
      return { ...i, discount: d, total_price: base, tax_amount: lineTax(base, 1, i.tax_rate) };
    }));
  };

  const updateTaxRate = (id: string, rate: number) => {
    const r = Math.min(100, Math.max(0, parseFloat(String(rate)) || 0));
    setBillItems(prev => prev.map(i => {
      if (i.id !== id) return i;
      return { ...i, tax_rate: r, tax_amount: lineTax(i.total_price, 1, r) };
    }));
  };

  const removeItem = (id: string) => {
    setBillItems(prev => prev.filter(i => i.id !== id));
    setUnitPriceInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const addCustomItem = (name: string) => {
    const customId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    setBillItems(prev => [...prev, {
      id: customId, service_name: name,
      category: 'other', item_type: 'other',
      quantity: 1, unit_price: 0, discount: 0,
      tax_rate: 0, tax_amount: 0, total_price: 0,
    }]);
  };

  // ── Payment operations ────────────────────────────────────────────────────
  const addPaymentRow = () => {
    setPayments(prev => {
      const used = new Set(prev.map(p => p.mode));
      const nextMode = PAYMENT_METHODS.find(m => !used.has(m)) ?? 'Cash';
      const remaining = Math.max(0, totalToCollect - prev.reduce((s, p) => s + (p.amount || 0), 0));
      return [...prev, makePaymentRow(nextMode, remaining)];
    });
  };

  // One click: pay as much of the bill as the advance covers, the rest stays on the other mode
  const applyAdvance = () => {
    setPayments(prev => {
      const adv = Math.min(availableAdvance, finalTotal);
      const rest = Math.max(0, totalToCollect - adv);
      const other = prev.find(p => p.mode !== 'Advance') ?? makePaymentRow();
      return rest > 0 ? [makePaymentRow('Advance', adv), { ...other, amount: rest }] : [makePaymentRow('Advance', adv)];
    });
  };

  const stopUsingAdvance = () => {
    setPayments(prev => {
      const rest = prev.filter(p => p.mode !== 'Advance');
      if (rest.length === 0) return [makePaymentRow('Cash', totalToCollect)];
      if (rest.length === 1) return [{ ...rest[0], amount: totalToCollect }];
      return rest;
    });
  };

  const removePaymentRow = (id: string) => {
    setPayments(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);
  };

  const updatePaymentRow = (id: string, patch: Partial<PaymentRowState>) => {
    setPayments(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  // Two rows that cover the bill stay covering it: "₹200 by UPI" leaves the rest on the other row
  const setRowAmount = (id: string, amount: number) => {
    setPayments(prev => {
      const other = prev.length === 2 ? prev.find(p => p.id !== id) : undefined;
      const balanced = prev.reduce((s, p) => s + (p.amount || 0), 0) === totalToCollect;
      if (!other || !balanced || other.mode === 'Advance') return prev.map(p => p.id === id ? { ...p, amount } : p);
      return prev.map(p => p.id === id ? { ...p, amount } : { ...p, amount: Math.max(0, totalToCollect - amount) });
    });
  };

  const setRowToFull = (id: string) => {
    setPayments(prev => {
      const row = prev.find(p => p.id === id);
      const others = prev.filter(p => p.id !== id).reduce((s, p) => s + (p.amount || 0), 0);
      const remaining = Math.max(0, totalToCollect - others);
      const cap = row?.mode === 'Advance' ? Math.min(remaining, availableAdvance) : remaining;
      return prev.map(p => p.id === id ? { ...p, amount: cap } : p);
    });
  };

  // ── Filtered services ─────────────────────────────────────────────────────
  const filteredServices = useMemo(() => {
    const list = services.filter(s => s.is_active !== false);
    if (!serviceSearch) return list;
    const q = serviceSearch.toLowerCase();
    return list.filter(s => s.name.toLowerCase().includes(q) || s.category?.toLowerCase().includes(q));
  }, [services, serviceSearch]);



  // ── Submit ────────────────────────────────────────────────────────────────
  const handleCreate = () => {
    if (!selectedPatient?.id) { setError('Please select a patient'); return; }
    if (billDate > localToday()) { setError('Bill date cannot be in the future'); return; }  // Bug 9: fresh date each time
    if (isFrontDesk && !selectedDoctorId) { setError('Please select a doctor'); return; }
    if (billItems.length === 0) { setError('Add at least one service'); return; }
    if (billItems.some(i => !i.service_name || i.unit_price < 0 || i.quantity <= 0)) {
      setError('Some items have invalid data'); return;
    }
    if (anyDiscount && !discountReason.trim()) { setError('Give a reason for the discount'); return; }

    // Bug 7: include deposit in overpayment error text
    if (trueOverpaid > 0) {
      const parts = ['bill total'];
      if (balanceForward > 0) parts.push('balance forward');
      if (depositAmount > 0)  parts.push('deposit');
      setError(`Received amount exceeds ${parts.join(' + ')}`); return;
    }

    // Bug 3: deposit cannot be collected if all payment is via advance (circular)
    if (depositAmount > 0 && !payments.some(p => p.mode !== 'Advance' && p.amount > 0)) {
      setError('Cannot collect deposit using advance balance — add a cash/UPI/card payment'); return;
    }

    // Bug 1: deposit must be covered by the payment amount after bill + settlement
    if (depositAmount > 0) {
      const depositCovered = Math.max(0, paidAmount - billPaidAmount - settlementAmount);
      if (depositCovered < depositAmount) {
        setError('Deposit not covered — payment must cover the bill total before a deposit can be collected'); return;
      }
    }

    const advanceTotal = payments.filter(p => p.mode === 'Advance').reduce((s, p) => s + (p.amount || 0), 0);
    // Bug 2: settlement only draws from advance if there are NO non-advance payment rows
    const hasNonAdvancePayment = payments.some(p => p.mode !== 'Advance' && p.amount > 0);
    const advanceSettlement = (!hasNonAdvancePayment && settlementAmount > 0) ? settlementAmount : 0;
    if (advanceTotal > availableAdvance) { setError('Advance amount exceeds available balance'); return; }
    if (advanceTotal + advanceSettlement > availableAdvance) { setError('Total advance usage (bill + dues) exceeds available balance'); return; }
    setError(null);

    // Cap payment rows at finalTotal FIFO — any excess is the settlement portion
    const billPayments: typeof payments = [];
    let remaining = finalTotal;
    for (const p of payments.filter(r => r.amount > 0)) {
      if (remaining <= 0) break;
      const take = Math.min(p.amount, remaining);
      billPayments.push({ ...p, amount: take });
      remaining -= take;
    }

    // Primary non-advance mode used for the settlement entry
    const primaryMode = billPayments.find(p => p.mode !== 'Advance')?.mode ?? payments[0]?.mode ?? 'Cash';

    onCreateInvoice({
      items: billItems.map(({ id: _id, discount, ...rest }) => ({ ...rest, discount_amount: discount })),
      discount: Math.round(discountAmount * 100) / 100, discountType, taxAmount: taxTotal,
      discountReason: anyDiscount ? discountReason.trim() : undefined,
      paidAmount: billPaidAmount,
      payments: billPayments.map(p => ({
        amount: p.amount,
        payment_mode: (p.mode === 'Advance' ? 'advance' : p.mode.toLowerCase()) as any,
        reference_number: p.referenceNumber.trim() || undefined,
        upi_app: p.mode === 'UPI' ? (p.upiApp || undefined) : undefined,
      })),
      finalTotal, invoiceDate: billDate,
      doctorId: selectedDoctorId || undefined,
      patient: selectedPatient,
      notes: notes.trim() || undefined,
      balance_forward:        balanceForward > 0 ? balanceForward : undefined,
      balance_forward_settle: settlementAmount > 0 ? settlementAmount : undefined,
      balance_forward_payment_mode: settlementAmount > 0 ? primaryMode.toLowerCase() : undefined,
      depositAmount:        depositAmount > 0 ? depositAmount : undefined,
      depositPaymentMode:   depositAmount > 0 ? primaryMode.toLowerCase() : undefined,
    });
  };

  const hasPatient = !!selectedPatient;
  const showDoctorDropdown = isFrontDesk;

  const statusTone: Record<typeof paymentStatus, StatusTone> = { Paid: 'success', Partial: 'warning', Pending: 'neutral' };
  const statusLabel: Record<typeof paymentStatus, string> = { Paid: 'Paid', Partial: 'Part paid', Pending: 'Unpaid' };

  // Service list under the add box: matches, then "Add … as a custom item" while something is typed
  const customName = serviceSearch.trim();
  const optionCount = filteredServices.length + (customName ? 1 : 0);
  const pickOption = (index: number) => {
    if (index < filteredServices.length) addService(filteredServices[index]);
    else if (customName) addCustomItem(customName);
    else return;
    setServiceSearch('');
    setServiceActive(0);
    serviceSearchRef.current?.focus();
  };
  const onServiceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setServiceDropdownOpen(true); setServiceActive(i => Math.min(optionCount - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setServiceActive(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (serviceDropdownOpen && optionCount > 0) pickOption(serviceActive); }
    else if (e.key === 'Escape' && serviceDropdownOpen) setServiceDropdownOpen(false);
  };

  const grid = hasGST ? ITEM_GRID_GST : ITEM_GRID;
  const hasAdvanceRow = payments.some(p => p.mode === 'Advance');
  const ageStr = selectedPatient
    ? displayAge(typeof selectedPatient.age === 'number' ? selectedPatient.age : undefined, selectedPatient.date_of_birth)
    : '';

  const renderItem = (item: BillItem, idx: number) => {
    const qty = (
      <input type="number" min={1} inputMode="numeric" aria-label={`Quantity of ${item.service_name}`}
        value={item.quantity} onKeyDown={noSignKeys}
        onChange={e => updateQty(item.id, parseInt(e.target.value) || 1)}
        className={cellInput(stacked)} />
    );
    const price = (
      <input type="number" min={0} inputMode="decimal" aria-label={`Price of ${item.service_name}`}
        value={unitPriceInputs[item.id] ?? String(item.unit_price)}
        onKeyDown={noSignKeys}
        onChange={e => {
          const raw = e.target.value;
          setUnitPriceInputs(prev => ({ ...prev, [item.id]: raw }));
          const parsed = parseFloat(raw);
          if (!isNaN(parsed) && parsed >= 0) updatePrice(item.id, parsed);
        }}
        onBlur={e => {
          const parsed = parseFloat(e.target.value);
          updatePrice(item.id, isNaN(parsed) || parsed < 0 ? 0 : parsed);
          setUnitPriceInputs(prev => { const n = { ...prev }; delete n[item.id]; return n; });
        }}
        placeholder="0" className={cellInput(stacked)} />
    );
    const gst = (
      <input type="number" min={0} max={100} inputMode="decimal" aria-label={`GST % on ${item.service_name}`}
        value={item.tax_rate === 0 ? '' : item.tax_rate} onKeyDown={noSignKeys}
        onChange={e => updateTaxRate(item.id, parseFloat(e.target.value) || 0)}
        placeholder="0" className={cellInput(stacked)} />
    );
    const discountCell = canDiscount ? (
      <input type="number" min={0} inputMode="decimal" aria-label={`Discount on ${item.service_name}`}
        value={item.discount === 0 ? '' : item.discount} onKeyDown={noSignKeys}
        onChange={e => updateDiscount(item.id, parseFloat(e.target.value) || 0)}
        placeholder="0" className={cellInput(stacked)} />
    ) : (
      <span className="block px-2 text-right font-mono text-sm tabular-nums text-muted-foreground">
        {item.discount > 0 ? inr(item.discount) : '—'}
      </span>
    );
    const amount = (
      <div className="text-right">
        <div className="font-mono text-sm font-semibold tabular-nums text-foreground">{inr(item.total_price)}</div>
        {item.tax_amount > 0 && <div className="text-xs text-muted-foreground font-mono tabular-nums">+{inr(item.tax_amount)} GST</div>}
      </div>
    );
    const remove = (
      <button type="button" onClick={() => removeItem(item.id)} aria-label={`Remove ${item.service_name}`}
        className="h-7 w-7 touch:h-10 touch:w-10 shrink-0 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10">
        <X className="w-4 h-4" />
      </button>
    );
    const name = (
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate" title={item.service_name}>{item.service_name}</div>
        {item.category && item.category !== 'other' && <div className="text-xs text-muted-foreground capitalize truncate">{item.category}</div>}
      </div>
    );

    if (stacked) {
      const label = (t: string) => <span className="block mb-1 text-xs text-muted-foreground">{t}</span>;
      return (
        <div key={item.id} className="px-3 py-2.5 space-y-2">
          <div className="flex items-start gap-2">
            <span className="w-5 pt-0.5 text-sm text-muted-foreground tabular-nums shrink-0">{idx + 1}</span>
            <div className="flex-1 min-w-0">{name}</div>
            {amount}
            {remove}
          </div>
          <div className={cn('grid gap-2 pl-7', hasGST ? 'grid-cols-4' : 'grid-cols-3')}>
            <div>{label('Qty')}{qty}</div>
            <div>{label('Price')}{price}</div>
            {hasGST && <div>{label('GST %')}{gst}</div>}
            <div>{label('Discount')}{discountCell}</div>
          </div>
        </div>
      );
    }
    return (
      <div key={item.id} className={cn(grid, 'min-h-10 py-0.5 items-center')}>
        <span className="text-sm text-muted-foreground tabular-nums">{idx + 1}</span>
        {name}
        {qty}
        {price}
        {hasGST && gst}
        {discountCell}
        {amount}
        {remove}
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        showClose={false}
        // Escape in the service box closes its list, not the bill
        onEscapeKeyDown={e => { if (serviceDropdownOpen) e.preventDefault(); }}
        className={cn('max-w-[1080px] w-full sm:w-[calc(100%-2rem)] p-0 gap-0 flex flex-col overflow-hidden rounded-lg bg-card',
          // Until a patient is chosen the dialog is only as tall as the search and its results
          hasPatient ? 'h-[90vh] max-h-[800px]' : 'max-h-[90vh]')}
      >
        {/* ── Header: title, patient, doctor and date ───────────────────────── */}
        <div className="relative flex flex-wrap items-start gap-x-4 gap-y-3 pl-4 sm:pl-6 pr-14 py-4 border-b border-border shrink-0">
          <div className="flex-1 min-w-[220px]">
            <DialogTitle className="text-base font-semibold text-foreground">New bill</DialogTitle>
            {hasPatient ? (
              <DialogDescription asChild>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-label">
                  <span className="font-medium text-foreground capitalize">{selectedPatient!.name}</span>
                  {(ageStr || selectedPatient!.gender) && (
                    <span>{[ageStr, selectedPatient!.gender].filter(Boolean).join(' · ')}</span>
                  )}
                  {selectedPatient!.mrn && <span>MRN <span className="font-mono text-foreground">{selectedPatient!.mrn}</span></span>}
                  {outstanding > 0 && balanceForward === 0 && <StatusChip tone="warning">{inr(outstanding)} due</StatusChip>}
                  {availableAdvance > 0 && <StatusChip tone="neutral">{inr(availableAdvance)} advance</StatusChip>}
                  {lastConsult && lastConsultDays != null && (
                    <span className={cn('text-xs', lastConsultDays === 0 ? 'font-medium text-warning' : 'text-muted-foreground')}>
                      Consultation {lastConsultDays === 0 ? 'already billed today' : `billed ${lastConsultDays} day${lastConsultDays === 1 ? '' : 's'} ago`}
                      {' · '}{inr(lastConsult.amount)}{lastConsult.doctor ? ` · ${lastConsult.doctor.startsWith('Dr.') ? lastConsult.doctor : `Dr. ${lastConsult.doctor}`}` : ''}
                    </span>
                  )}
                  {pendingAmount > 0 && outstanding === 0 && availableAdvance > 0 && (
                    <span className="text-xs text-muted-foreground">covers {inr(pendingAmount)} unpaid on earlier bills</span>
                  )}
                  {!patient && (
                    <button type="button"
                      onClick={() => { setSelectedPatient(null); setBillItems([]); setPayments([makePaymentRow()]); setDiscount(0); setBalanceForward(0); }}
                      className="text-[13px] font-medium text-primary hover:underline underline-offset-2">
                      Change
                    </button>
                  )}
                </div>
              </DialogDescription>
            ) : (
              <DialogDescription className="mt-1 text-[13px] text-label">Choose the patient to bill.</DialogDescription>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            {showDoctorDropdown && (
              <label className="block">
                <span className="block mb-1 text-[13px] font-semibold text-label">Doctor</span>
                <select value={selectedDoctorId} onChange={e => setSelectedDoctorId(e.target.value)}
                  className={cn(selectClass, 'w-44')}>
                  <option value="">{doctors.length ? 'Select a doctor' : 'No doctors'}</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>{d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="block">
              <span className="flex items-baseline justify-between gap-2 mb-1 text-[13px] font-semibold text-label">
                Bill date
                {billDate !== todayStr && (
                  <button type="button" onClick={() => setBillDate(localToday())}
                    className="font-medium text-primary hover:underline underline-offset-2">Today</button>
                )}
              </span>
              <Input type="date" value={billDate} max={todayStr} onChange={e => setBillDate(e.target.value)}
                className="w-40 font-mono tabular-nums" />
            </label>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="absolute right-3 top-3">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* ── Alerts ───────────────────────────────────────────────────────── */}
        {(isBackdated && hasPatient) || error ? (
          <div className="px-4 sm:px-6 pt-3 space-y-2 shrink-0">
            {isBackdated && hasPatient && (
              <div className="flex items-center gap-2 rounded-md border border-highlight-foreground/40 bg-highlight px-3 py-2 text-sm text-highlight-foreground">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Dated {formatDate(billDate)}, more than 30 days ago. Check the date before saving.
              </div>
            )}
            {error && (
              <div role="alert" className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span className="flex-1">{error}</span>
                <button type="button" onClick={() => setError(null)} aria-label="Dismiss" className="touch-target rounded p-0.5 hover:bg-destructive/10">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : null}

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        {!hasPatient ? (

          /* ── Patient search ── */
          <div className="flex-1 min-h-[280px] overflow-y-auto px-4 sm:px-6 py-8">
            <div className="w-full max-w-md mx-auto">
              <label htmlFor="bill-patient-search" className="block mb-1.5 text-[13px] font-semibold text-label">Patient</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="bill-patient-search"
                  ref={patientInputRef}
                  value={patientQuery}
                  onChange={e => setPatientQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && patientResults[0]) {
                      e.preventDefault();
                      pickPatient(patientResults[0]);
                    }
                  }}
                  placeholder="Name or phone number"
                  className="pl-9 pr-9 bg-card"
                  autoComplete="off"
                  autoFocus
                />
                {patientSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {patientResults.length > 0 ? (
                <div className="mt-2 rounded-md border border-border bg-card divide-y divide-divider overflow-hidden">
                  {patientResults.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => pickPatient(p)}
                      className="w-full flex items-center gap-3 px-3 min-h-11 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none">
                      <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-sm font-semibold">
                        {p.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground capitalize truncate">{p.name}</span>
                        <span className="block text-xs text-muted-foreground truncate">
                          {[displayAge(typeof p.age === 'number' ? p.age : undefined, p.date_of_birth), p.gender, p.phone, p.mrn && `MRN ${p.mrn}`].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : patientQuery.trim().length >= 2 && !patientSearching ? (
                <p className="mt-2 text-sm text-muted-foreground">No patients match "{patientQuery.trim()}".</p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground">Type at least 2 letters or digits. Enter picks the first match.</p>
              )}
            </div>
          </div>

        ) : (

          /* ── Items | checkout ── */
          <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">

            {/* ══ Items ══ */}
            <div className="lg:flex-1 min-w-0 lg:overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
              <section aria-labelledby="bill-items-title">
                <h3 id="bill-items-title" className="mb-2 text-[15px] font-semibold text-foreground">
                  Services {billItems.length > 0 && <span className="ml-1 font-normal text-muted-foreground tabular-nums">{billItems.length}</span>}
                </h3>

                <div className="rounded-md border border-field bg-card">
                  {!stacked && (billItems.length > 0 || balanceForward > 0) && (
                    <div className={cn(grid, 'h-8 items-center rounded-t-md bg-input border-b border-border text-xs font-medium text-muted-foreground')}>
                      <span>#</span><span>Service</span>
                      <span className="px-2 text-right">Qty</span>
                      <span className="px-2 text-right">Price</span>
                      {hasGST && <span className="px-2 text-right">GST %</span>}
                      <span className="px-2 text-right">Discount</span>
                      <span className="text-right">Amount</span>
                      <span />
                    </div>
                  )}

                  <div className="divide-y divide-divider">
                    {billItems.map(renderItem)}

                    {/* Previous balance — added from the checkout; settled on save */}
                    {balanceForward > 0 && (
                      <div className={cn(stacked ? 'flex items-start gap-2 px-3 py-2.5' : cn(grid, 'min-h-10 py-0.5 items-center'))}>
                        <span className={cn('text-sm text-muted-foreground', stacked && 'w-5 shrink-0')}>—</span>
                        <div className={cn('min-w-0', stacked ? 'flex-1' : hasGST ? 'col-span-5' : 'col-span-4')}>
                          <div className="text-sm font-medium text-foreground">Previous balance</div>
                          <div className="text-xs text-muted-foreground">Due from earlier bills · settled when this bill is paid</div>
                        </div>
                        <div className="text-right font-mono text-sm font-semibold tabular-nums text-foreground">{inr(balanceForward)}</div>
                        <button type="button" onClick={() => setBalanceForward(0)} aria-label="Remove previous balance"
                          className="h-7 w-7 touch:h-10 touch:w-10 shrink-0 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Add a service — dashed box as the pad's "Type a medicine…" */}
                  <div className={cn('relative px-3 py-2', (billItems.length > 0 || balanceForward > 0) && 'border-t border-divider')}>
                    <div className="flex items-center gap-2 h-8 touch:h-10 rounded-md border border-dashed border-field bg-card px-2.5 focus-within:border-ring focus-within:border-solid focus-within:ring-2 focus-within:ring-ring/20">
                      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                      <input
                        ref={serviceSearchRef}
                        value={serviceSearch}
                        onChange={e => { setServiceSearch(e.target.value); setServiceActive(0); setServiceDropdownOpen(true); }}
                        onFocus={() => setServiceDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setServiceDropdownOpen(false), 150)}
                        onKeyDown={onServiceKeyDown}
                        placeholder="Type a service…"
                        aria-label="Add a service"
                        role="combobox"
                        aria-expanded={serviceDropdownOpen}
                        aria-controls="bill-service-list"
                        autoComplete="off"
                        autoFocus={billItems.length === 0}
                        className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
                      />
                      {serviceSearch && (
                        <button type="button" aria-label="Clear"
                          onMouseDown={e => { e.preventDefault(); setServiceSearch(''); setServiceActive(0); serviceSearchRef.current?.focus(); }}
                          className="touch-target shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {serviceDropdownOpen && (
                      <div ref={serviceDropdownRef} id="bill-service-list" role="listbox"
                        className="absolute left-3 right-3 sm:right-auto sm:w-[420px] top-full z-30 -mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover shadow-lg divide-y divide-divider">
                        {filteredServices.length === 0 && !customName && (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">No active services. Add them in Settings → Billing services.</div>
                        )}
                        {filteredServices.map((svc, i) => {
                          const inBill = billItems.find(b => b.service_id === svc.id);
                          return (
                            <button key={svc.id} type="button" role="option" aria-selected={i === serviceActive}
                              onMouseDown={e => { e.preventDefault(); pickOption(i); }}
                              onMouseEnter={() => setServiceActive(i)}
                              className={cn('w-full flex items-center gap-3 px-3 min-h-9 py-1.5 text-left',
                                i === serviceActive ? 'bg-accent' : 'hover:bg-accent')}>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm text-foreground truncate">{svc.name}</span>
                                {svc.category && <span className="block text-xs text-muted-foreground capitalize truncate">{svc.category}</span>}
                              </span>
                              {inBill && <StatusChip tone="accent">×{inBill.quantity}</StatusChip>}
                              <span className="font-mono text-sm tabular-nums text-foreground shrink-0">{inr(parseFloat(String(svc.price)) || 0)}</span>
                            </button>
                          );
                        })}
                        {customName && (
                          <button type="button" role="option" aria-selected={serviceActive === filteredServices.length}
                            onMouseDown={e => { e.preventDefault(); pickOption(filteredServices.length); }}
                            onMouseEnter={() => setServiceActive(filteredServices.length)}
                            className={cn('w-full flex items-center gap-2 px-3 min-h-9 py-1.5 text-left text-sm text-label',
                              serviceActive === filteredServices.length ? 'bg-accent' : 'hover:bg-accent')}>
                            <Plus className="w-4 h-4 shrink-0" />
                            <span className="truncate">Add <span className="font-medium text-foreground">"{customName}"</span> as a custom item</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {billItems.length === 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">Pick from the clinic's services, or type a name to add a custom item and set its price.</p>
                )}
              </section>

              <div>
                <label htmlFor="bill-notes" className="block mb-1.5 text-[13px] font-semibold text-label">
                  Notes <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <Input id="bill-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Shown on the bill" className="bg-card" />
              </div>
            </div>

            {/* ══ Checkout ══ */}
            <div className="w-full lg:w-[340px] shrink-0 flex flex-col lg:overflow-hidden bg-input border-t lg:border-t-0 lg:border-l border-border">
              <div className="flex-1 lg:overflow-y-auto px-4 sm:px-6 lg:px-5 py-5 space-y-5">

                {/* Earlier dues — the one account fact that changes what to collect here */}
                {outstanding > 0 && balanceForward === 0 && (
                  <div className="flex items-center gap-3 rounded-md border border-highlight-foreground/40 bg-highlight px-3 py-2">
                    <span className="flex-1 text-sm text-highlight-foreground">
                      <span className="font-mono font-semibold">{inr(outstanding)}</span> due from earlier bills
                    </span>
                    <Button type="button" size="sm" variant="outline" className="bg-card shrink-0" onClick={() => setBalanceForward(outstanding)}>
                      Add to bill
                    </Button>
                  </div>
                )}

                {/* Summary — Subtotal / Bill total only when something sits between them and the total */}
                <section aria-labelledby="bill-summary-title" className="space-y-2">
                  <h3 id="bill-summary-title" className="text-[15px] font-semibold text-foreground">Summary</h3>
                  {(discountAmount > 0 || taxTotal > 0 || discountOpen) && <SummaryLine label="Subtotal" value={inr(subtotal)} />}

                  {canDiscount && (discountOpen || discount > 0) && (
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-label">Discount</span>
                      <div className="flex items-center gap-1">
                        <Segmented
                          aria-label="Discount type"
                          options={[{ value: 'flat', label: '₹' }, { value: 'percent', label: '%' }]}
                          value={discountType}
                          onChange={t => { setDiscountType(t); setDiscount(d => t === 'percent' ? Math.min(d, 100) : Math.min(d, subtotal)); }}
                        />
                        <input
                          type="number" min={0} max={discountType === 'percent' ? 100 : subtotal} inputMode="decimal"
                          aria-label={discountType === 'percent' ? 'Discount percent' : 'Discount amount'}
                          value={discount === 0 ? '' : discount}
                          onKeyDown={noSignKeys}
                          onChange={e => {
                            const v = parseFloat(e.target.value) || 0;
                            setDiscount(discountType === 'percent' ? Math.min(100, v) : Math.min(subtotal, v));
                          }}
                          placeholder="0"
                          autoFocus={discount === 0}
                          className={cn(cellInput(true), 'w-20')}
                        />
                        <button type="button" onClick={() => { setDiscount(0); setDiscountOpen(false); }} aria-label="Remove discount"
                          className="h-7 w-7 touch:h-10 touch:w-10 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {discountAmount > 0 && (!canDiscount || discountType === 'percent') && (
                    <SummaryLine label={canDiscount ? 'Discount amount' : 'Discount'} value={`−${inr(discountAmount)}`} />
                  )}
                  {anyDiscount && (
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <label htmlFor="bill-discount-reason" className="text-label shrink-0">Discount reason</label>
                      <input
                        id="bill-discount-reason"
                        value={discountReason}
                        onChange={e => setDiscountReason(e.target.value)}
                        maxLength={200}
                        placeholder="e.g. Staff family, follow-up, senior citizen"
                        className="h-8 touch:h-10 w-full max-w-[16rem] min-w-0 rounded-md border border-field bg-card px-2 text-sm focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
                      />
                    </div>
                  )}
                  {taxTotal > 0 && <SummaryLine label="GST" value={inr(taxTotal)} />}
                  {(discountAmount > 0 || taxTotal > 0 || discountOpen || balanceForward > 0 || depositAmount > 0) && (
                    <SummaryLine label="Bill total" value={inr(finalTotal)} strong className="pt-2 border-t border-border" />
                  )}
                  {balanceForward > 0 && <SummaryLine label="Previous balance" value={inr(balanceForward)} />}

                  {/* Advance collected with this bill */}
                  {(depositOpen || depositAmount > 0) && (
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <label htmlFor="bill-advance" className="text-label">Advance to collect</label>
                      <div className="flex items-center gap-1">
                        <input
                          id="bill-advance"
                          type="number" min={0} inputMode="decimal"
                          value={depositAmount === 0 ? '' : depositAmount}
                          onKeyDown={noSignKeys}
                          onChange={e => setDepositAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                          placeholder="0"
                          autoFocus
                          className={cn(cellInput(true), 'w-28')}
                        />
                        <button type="button" onClick={() => { setDepositAmount(0); setDepositOpen(false); }} aria-label="Don't collect advance"
                          className="h-7 w-7 touch:h-10 touch:w-10 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {((canDiscount && !discountOpen && discount === 0) || (!depositOpen && depositAmount === 0)) && (
                    <div className="flex flex-wrap gap-x-4">
                      {canDiscount && !discountOpen && discount === 0 && (
                        <button type="button" onClick={() => setDiscountOpen(true)}
                          className="touch-target flex items-center gap-1 text-[13px] font-medium text-primary hover:underline underline-offset-2">
                          <Plus className="w-3.5 h-3.5" /> Discount
                        </button>
                      )}
                      {!depositOpen && depositAmount === 0 && (
                        <button type="button" onClick={() => setDepositOpen(true)}
                          className="touch-target flex items-center gap-1 text-[13px] font-medium text-primary hover:underline underline-offset-2">
                          <Plus className="w-3.5 h-3.5" /> Collect advance
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex items-baseline justify-between gap-3 pt-2 border-t border-border">
                    <span className="text-sm font-semibold text-foreground">
                      {(balanceForward > 0 || depositAmount > 0) ? 'To collect' : 'Total'}
                    </span>
                    <span className="font-mono text-2xl font-semibold tabular-nums text-foreground">{inr(totalToCollect)}</span>
                  </div>
                </section>

                {/* Payment */}
                <section aria-labelledby="bill-payment-title" className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 id="bill-payment-title" className="text-[15px] font-semibold text-foreground">Payment</h3>
                    <StatusChip tone={statusTone[paymentStatus]}>{statusLabel[paymentStatus]}</StatusChip>
                  </div>

                  {/* Advance on account: one click instead of a hidden payment mode */}
                  {availableAdvance > 0 && finalTotal > 0 && !hasAdvanceRow && (
                    <div className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2">
                      <span className="flex-1 text-sm text-label">
                        Advance <span className="font-mono font-semibold text-foreground">{inr(availableAdvance)}</span>
                      </span>
                      <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={applyAdvance}>
                        Use {inr(Math.min(availableAdvance, finalTotal))}
                      </Button>
                    </div>
                  )}

                  {payments.map(row => {
                    const split = payments.length > 1;
                    const isAdvance = row.mode === 'Advance';
                    // UPI and cheque numbers are usually recorded, so their box shows; cash and card behind "+ Reference"
                    const showRef = row.mode === 'UPI' || row.mode === 'Cheque' || !!row.referenceNumber || refOpen[row.id];
                    const refPlaceholder = {
                      UPI: 'Transaction ID', Cash: 'Receipt no.', Card: 'Card transaction ID', Cheque: 'Cheque no.',
                    }[row.mode];
                    return (
                      <div key={row.id} className={cn('space-y-1.5', split && 'rounded-md border border-border bg-card p-2.5')}>
                        {isAdvance ? (
                          <div className="flex items-center justify-between gap-2 text-[13px]">
                            <span className="font-medium text-foreground">From advance</span>
                            <span className="text-muted-foreground">{inr(availableAdvance)} available</span>
                          </div>
                        ) : (
                          <Segmented
                            aria-label="Payment mode"
                            className="w-full"
                            options={PAYMENT_METHODS.map(m => ({ value: m, label: m }))}
                            value={row.mode}
                            onChange={m => updatePaymentRow(row.id, { mode: m, referenceNumber: '', upiApp: '' })}
                          />
                        )}
                        <div className="flex items-center gap-1.5">
                          <div className="relative flex-1 min-w-0">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">₹</span>
                            <input
                              type="number" min={0} inputMode="decimal" aria-label={`${row.mode} amount`}
                              max={isAdvance ? availableAdvance : undefined}
                              value={row.amount === 0 ? '' : row.amount}
                              onKeyDown={noSignKeys}
                              onChange={e => {
                                const v = parseFloat(e.target.value);
                                const raw = isNaN(v) ? 0 : Math.max(0, v);
                                setRowAmount(row.id, isAdvance ? Math.min(raw, availableAdvance) : raw);
                              }}
                              placeholder="0"
                              className={cn(cellInput(true), 'h-9 pl-6 font-semibold')}
                            />
                          </div>
                          {split && (
                            <Button type="button" variant="outline" size="sm" className="h-9 bg-card shrink-0" onClick={() => setRowToFull(row.id)}
                              title="Fill the amount still to collect">
                              Full
                            </Button>
                          )}
                          {(split || isAdvance) && (
                            <button type="button" onClick={() => isAdvance ? stopUsingAdvance() : removePaymentRow(row.id)}
                              aria-label={isAdvance ? "Don't use advance" : `Remove ${row.mode} payment`}
                              className="h-7 w-7 touch:h-10 touch:w-10 shrink-0 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {row.mode === 'UPI' && (
                          <div className="flex gap-1.5">
                            <select aria-label="UPI app" value={row.upiApp}
                              onChange={e => updatePaymentRow(row.id, { upiApp: e.target.value })}
                              className={cn(selectClass, 'h-8 w-[104px] shrink-0 text-[13px]')}>
                              <option value="">UPI app</option>
                              {['gpay', 'phonepe', 'paytm', 'bhim', 'other'].map(a => (
                                <option key={a} value={a}>{a === 'gpay' ? 'GPay' : a === 'phonepe' ? 'PhonePe' : a === 'bhim' ? 'BHIM' : a.charAt(0).toUpperCase() + a.slice(1)}</option>
                              ))}
                            </select>
                            <Input value={row.referenceNumber} onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                              placeholder={refPlaceholder} aria-label="UPI transaction ID" className="h-8 bg-card font-mono text-[13px]" />
                          </div>
                        )}
                        {(row.mode === 'Cash' || row.mode === 'Card' || row.mode === 'Cheque') && (
                          showRef ? (
                            <Input value={row.referenceNumber} onChange={e => updatePaymentRow(row.id, { referenceNumber: e.target.value })}
                              placeholder={row.mode === 'Cheque' ? refPlaceholder : `${refPlaceholder} (optional)`} aria-label={refPlaceholder}
                              autoFocus={!!refOpen[row.id] && !row.referenceNumber}
                              className="h-8 bg-card font-mono text-[13px]" />
                          ) : (
                            <button type="button" onClick={() => setRefOpen(o => ({ ...o, [row.id]: true }))}
                              className="touch-target flex items-center gap-1 text-[13px] text-label hover:text-foreground">
                              <Plus className="w-3.5 h-3.5" /> {row.mode === 'Cash' ? 'Receipt no.' : 'Transaction ID'}
                            </button>
                          )
                        )}
                      </div>
                    );
                  })}

                  {payments.filter(p => p.mode !== 'Advance').length < PAYMENT_METHODS.length && (
                    <button type="button" onClick={addPaymentRow}
                      className="touch-target flex items-center gap-1 text-[13px] font-medium text-primary hover:underline underline-offset-2">
                      <Plus className="w-3.5 h-3.5" /> Split payment
                    </button>
                  )}

                  {(payments.length > 1 || balanceAmount > 0 || trueOverpaid > 0 || settlementAmount > 0) && (
                    <div className="space-y-1 pt-2 border-t border-border">
                      <SummaryLine label="Received" value={inr(paidAmount)} />
                      {balanceAmount > 0 && <SummaryLine label="Balance" value={inr(balanceAmount)} />}
                      {settlementAmount > 0 && (
                        <p className="text-xs text-success">{inr(settlementAmount)} clears earlier dues</p>
                      )}
                      {trueOverpaid > 0 && (
                        <p className="text-xs font-medium text-destructive">{inr(trueOverpaid)} more than the amount to collect</p>
                      )}
                      {trueOverpaid === 0 && settlementAmount === 0 && paidAmount > 0 && balanceAmount > 0 && (
                        <p className="text-xs text-highlight-foreground">{inr(balanceAmount)} stays due on this bill</p>
                      )}
                    </div>
                  )}
                </section>
              </div>

              {/* ── Actions — pinned; the button says what will be saved ── */}
              <div className="flex gap-2 px-4 sm:px-6 lg:px-5 py-3 border-t border-border bg-card shrink-0 sticky bottom-0 lg:static">
                <Button type="button" variant="outline" onClick={onClose} disabled={loading} className="shrink-0 px-3">
                  Cancel
                </Button>
                <Button type="button" onClick={handleCreate}
                  disabled={loading || billItems.length === 0 || !hasPatient || trueOverpaid > 0}
                  className="flex-1 min-w-0 gap-1.5">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : (
                    <span className="truncate">
                      {billItems.length === 0 || trueOverpaid > 0 ? 'Create bill'
                        : paidAmount <= 0 ? 'Create unpaid bill'
                        : balanceAmount > 0 ? `Create bill · ${inrShort(balanceAmount)} due`
                        : `Create bill · ${inrShort(paidAmount)} paid`}
                    </span>
                  )}
                </Button>
              </div>
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
